'use client';

import { useEffect, useRef, useState } from 'react';

export default function ContactSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [form, setForm] = useState({
    name: '', email: '', company: '', service: '', budget: '', message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { threshold: 0.1 }
    );
    sectionRef.current?.querySelectorAll('.reveal-hidden, .reveal-left, .reveal-right').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Connect to backend email service or API
    setSubmitted(true);
  };

  return (
    <section id="contact" ref={sectionRef} className="py-24 lg:py-32" style={{ background: '#fff' }}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16 reveal-hidden">
          <span className="section-label">Get in Touch</span>
          <div className="section-divider mx-auto" />
          <h2
            className="font-heading font-extrabold leading-tight mb-4"
            style={{ fontSize: 'clamp(2rem, 3.5vw, 2.8rem)', color: '#0D1A3A', letterSpacing: '-0.025em' }}
          >
            Ready to Start Your Project?
          </h2>
          <p className="text-base max-w-lg mx-auto" style={{ color: '#6B7280' }}>
            Fill out the form below and we&apos;ll respond within 1 business day with a free consultation offer.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12 items-start">
          {/* Contact Info */}
          <div className="lg:col-span-2 reveal-left">
            <div className="space-y-6">
              {[
                {
                  icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                    </svg>
                  ),
                  label: 'Email',
                  value: 'contact@cartelix-llc.com',
                  href: 'mailto:contact@cartelix-llc.com',
                },
                {
                  icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 8.63 19.79 19.79 0 01.1 2.18 2 2 0 012.08 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006.09 6.09l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                    </svg>
                  ),
                  label: 'Phone',
                  value: '+1 (334) 583-1026',
                  href: 'tel:+13345831026',
                },
                {
                  icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                    </svg>
                  ),
                  label: 'Address',
                  value: '30 N Gould St Ste R\nSheridan, WY 82801\nWyoming, USA',
                  href: undefined,
                },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: '#1F3A8A' }}
                  >
                    {item.icon}
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#9CA3AF' }}>
                      {item.label}
                    </p>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="text-sm font-semibold hover:text-brand-primary transition-colors"
                        style={{ color: '#0D1A3A', fontFamily: '"DM Sans",sans-serif' }}
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-sm font-semibold whitespace-pre-line" style={{ color: '#0D1A3A', fontFamily: '"DM Sans",sans-serif' }}>
                        {item.value}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Business hours */}
            <div
              className="mt-8 p-5 rounded-2xl"
              style={{ background: '#EEF2FF', border: '1px solid rgba(31,58,138,0.12)' }}
            >
              <p className="font-heading font-bold text-sm mb-3" style={{ color: '#0D1A3A' }}>Response Time</p>
              <p className="text-sm" style={{ color: '#6B7280', lineHeight: 1.7 }}>
                We respond to all inquiries within <strong style={{ color: '#1F3A8A' }}>1 business day</strong>.<br />
                Available Monday – Friday, 9:00 AM – 6:00 PM EST.
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3 reveal-right">
            {submitted ? (
              <div
                className="p-10 rounded-2xl text-center"
                style={{ background: '#EEF2FF', border: '1px solid rgba(31,58,138,0.15)' }}
              >
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: '#1F3A8A' }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                </div>
                <h3 className="font-heading font-bold text-xl mb-2" style={{ color: '#0D1A3A' }}>Message Received!</h3>
                <p style={{ color: '#6B7280', fontSize: '15px' }}>
                  Thank you for reaching out. We&apos;ll respond within 1 business day at {form.email}.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="p-8 rounded-2xl space-y-5"
                style={{ background: '#F8FAFF', border: '1px solid #E5E9F5' }}
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="form-label">Full Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="John Smith"
                      className="form-field"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="form-label">Email Address *</label>
                    <input
                      type="email"
                      required
                      placeholder="john@company.com"
                      className="form-field"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="form-label">Company / Organization</label>
                    <input
                      type="text"
                      placeholder="Your company name"
                      className="form-field"
                      value={form.company}
                      onChange={(e) => setForm({ ...form, company: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="form-label">Service Needed *</label>
                    <select
                      required
                      className="form-field"
                      value={form.service}
                      onChange={(e) => setForm({ ...form, service: e.target.value })}
                      style={{ appearance: 'none' }}
                    >
                      <option value="">Select a service</option>
                      <option value="website">Website Creation</option>
                      <option value="app">Mobile App</option>
                      <option value="social">Social Media Posts</option>
                      <option value="branding">Rebranding</option>
                      <option value="multiple">Multiple Services</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="form-label">Estimated Budget (USD)</label>
                  <select
                    className="form-field"
                    value={form.budget}
                    onChange={(e) => setForm({ ...form, budget: e.target.value })}
                    style={{ appearance: 'none' }}
                  >
                    <option value="">Select a budget range</option>
                    <option value="under-1k">Under $1,000</option>
                    <option value="1k-5k">$1,000 – $5,000</option>
                    <option value="5k-15k">$5,000 – $15,000</option>
                    <option value="15k-50k">$15,000 – $50,000</option>
                    <option value="50k+">$50,000+</option>
                  </select>
                </div>

                <div>
                  <label className="form-label">Project Description *</label>
                  <textarea
                    required
                    rows={4}
                    placeholder="Tell us about your project — goals, timeline, any specific requirements..."
                    className="form-field"
                    style={{ resize: 'vertical' }}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                  />
                </div>

                <div className="flex items-start gap-3">
                  <div
                    className="flex-shrink-0 w-4 h-4 mt-0.5 rounded border"
                    style={{ border: '1.5px solid #D1D5DB', background: '#fff' }}
                  />
                  <p style={{ fontSize: '12px', color: '#9CA3AF', lineHeight: 1.6 }}>
                    By submitting this form, you agree to our{' '}
                    <span style={{ color: '#1F3A8A', fontWeight: 600, cursor: 'pointer' }}>Privacy Policy</span> and{' '}
                    <span style={{ color: '#1F3A8A', fontWeight: 600, cursor: 'pointer' }}>Terms of Service</span>.
                    We do not share your information with third parties.
                  </p>
                </div>

                <button type="submit" className="btn-primary w-full justify-center" style={{ padding: '15px 24px' }}>
                  Send Message
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}